import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { decodeHtmlEntities } from "../../utils/htmlEntities";

const KIND_LABELS = {
  movement: "Movimentação",
  intimation: "Intimação",
  publication: "Publicação",
  intermediate: "Intermediária",
};

/** Categorias visuais da timeline (cor do ponto + badge). */
const MOVEMENT_VISUAL_STYLES = {
  movement: {
    dot: "bg-blue-600",
    line: "bg-blue-200",
    badge: "bg-blue-50 text-blue-700 border-blue-200",
    legend: "Movimentação",
  },
  publication: {
    dot: "bg-violet-600",
    line: "bg-violet-200",
    badge: "bg-violet-50 text-violet-800 border-violet-200",
    legend: "Publicação",
  },
  intimation: {
    dot: "bg-amber-500",
    line: "bg-amber-200",
    badge: "bg-amber-50 text-amber-900 border-amber-200",
    legend: "Intimação",
  },
  intermediate: {
    dot: "bg-slate-500",
    line: "bg-slate-200",
    badge: "bg-slate-100 text-slate-700 border-slate-300",
    legend: "Intermediária",
  },
};

const LEGEND_ORDER = [
  "movement",
  "publication",
  "intimation",
  "intermediate",
];

function normalizeMovementText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Define a paleta com base no kind da API. */
function getMovementVisualCategory(movement) {
  const descriptionText = normalizeMovementText(movement.description);

  if (movement.kind === "publication") {
    return "publication";
  }
  if (movement.kind === "intimation") {
    return "intimation";
  }
  if (movement.kind === "intermediate") {
    return "intermediate";
  }

  if (descriptionText.includes("publicado no djen")) {
    return "publication";
  }

  return "movement";
}

function formatMovementDate(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatMovementDateTime(dateString) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function getKindLabel(kind) {
  if (!kind) return "Movimentação";
  return KIND_LABELS[kind] || kind;
}

function getMovementTitle(movement) {
  if (movement.class_process?.trim()) {
    return decodeHtmlEntities(movement.class_process.trim());
  }
  return getKindLabel(movement.kind);
}

function normalizeInstanceLabel(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** 1º grau, 2º grau, instâncias superiores e demais, nesta ordem. */
function instanceSortRank(instanceName) {
  const label = normalizeInstanceLabel(instanceName);
  if (/\b1\b|primeiro/.test(label)) return 0;
  if (/\b2\b|segundo/.test(label)) return 1;
  if (/superior|stj|stf|tst/.test(label)) return 2;
  if (label.includes("nao inform")) return 4;
  return 3;
}

function groupByInstance(movements) {
  const groups = new Map();

  for (const movement of movements) {
    const key = movement.instance_name?.trim() || "Instância não informada";
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(movement);
  }

  return Array.from(groups.entries())
    .map(([instanceName, items]) => ({
      instanceName,
      items,
    }))
    .sort(
      (a, b) =>
        instanceSortRank(a.instanceName) - instanceSortRank(b.instanceName),
    );
}

function InstanceSection({ instanceName, items, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-sm font-semibold text-gray-900">
          Instância: {instanceName}
        </span>
        <span className="flex items-center gap-2 text-xs text-gray-600">
          {items.length} movimentaç{items.length === 1 ? "ão" : "ões"}
          {expanded ? (
            <ChevronUp className="w-4 h-4" aria-hidden />
          ) : (
            <ChevronDown className="w-4 h-4" aria-hidden />
          )}
        </span>
      </button>

      {expanded && (
        <div className="px-4 py-4 bg-white">
          <ol className="relative space-y-0">
            {items.map((movement, index) => {
              const description = decodeHtmlEntities(
                [movement.description, movement.complement]
                  .filter(Boolean)
                  .join(" — "),
              );
              const isLast = index === items.length - 1;
              const category = getMovementVisualCategory(movement);
              const visual =
                MOVEMENT_VISUAL_STYLES[category] ||
                MOVEMENT_VISUAL_STYLES.movement;
              const nextCategory = !isLast
                ? getMovementVisualCategory(items[index + 1])
                : null;
              const nextLine =
                (nextCategory && MOVEMENT_VISUAL_STYLES[nextCategory]?.line) ||
                visual.line;

              return (
                <li
                  key={movement.id || `${movement.index_movement}-${index}`}
                  className="relative flex gap-4 pb-8"
                >
                  {!isLast && (
                    <span
                      className={`absolute left-[4.35rem] top-3 bottom-0 w-px ${nextLine}`}
                      aria-hidden
                    />
                  )}

                  <time
                    dateTime={movement.date_event}
                    className="w-24 flex-shrink-0 pt-0.5 text-xs font-medium text-gray-500 text-right"
                  >
                    {formatMovementDate(movement.date_event)}
                  </time>

                  <span
                    className={`relative z-10 mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ring-4 ring-white ${visual.dot}`}
                    aria-hidden
                  />

                  <div className="min-w-0 flex-1 -mt-0.5">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {getMovementTitle(movement)}
                      </h4>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${visual.badge}`}
                      >
                        {getKindLabel(movement.kind)}
                      </span>
                      {movement.system_name && (
                        <span className="text-xs text-gray-500">
                          {movement.system_name}
                        </span>
                      )}
                    </div>
                    {description ? (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {description}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        Sem descrição
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function ProcessMovementsPanel({
  movementsResponse,
  loading,
  error,
  onLoadMore,
  loadingMore,
  isExpanded,
  onToggle,
}) {
  const movements = movementsResponse?.data || [];
  const meta = movementsResponse?.meta;

  const grouped = useMemo(() => groupByInstance(movements), [movements]);

  const lastMovementDate = useMemo(() => {
    if (!movements.length) return null;
    const sorted = [...movements].sort(
      (a, b) =>
        new Date(b.date_event).getTime() - new Date(a.date_event).getTime(),
    );
    return sorted[0]?.date_event;
  }, [movements]);

  const hasMore = meta && meta.current_page < meta.last_page;

  const renderBody = () => {
    if (loading && !movements.length) {
      return (
        <div className="flex items-center justify-center py-10 gap-2 text-gray-600">
          <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-sm">Carregando movimentações...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Não foi possível carregar as movimentações
            </p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      );
    }

    if (!movements.length) {
      return (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertCircle
            className="w-5 h-5 text-amber-600 flex-shrink-0"
            aria-hidden
          />
          <p className="text-sm font-semibold text-amber-800">
            Não foram Encontradas Movimentações para este processo!
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {lastMovementDate && (
          <p className="text-xs text-gray-500">
            Última movimentação:{" "}
            <span className="font-medium text-gray-700">
              {formatMovementDateTime(lastMovementDate)}
            </span>
            {meta?.total != null && (
              <>
                {" "}
                · {meta.total} movimentaç{meta.total === 1 ? "ão" : "ões"} no
                total
              </>
            )}
          </p>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-2 pb-1">
          {LEGEND_ORDER.map((key) => (
            <span
              key={key}
              className="inline-flex items-center gap-1.5 text-xs text-gray-600"
            >
              <span
                className={`h-2 w-2 rounded-full ${MOVEMENT_VISUAL_STYLES[key].dot}`}
                aria-hidden
              />
              {MOVEMENT_VISUAL_STYLES[key].legend}
            </span>
          ))}
        </div>

        {grouped.map(({ instanceName, items }, index) => (
          <InstanceSection
            key={instanceName}
            instanceName={instanceName}
            items={items}
            defaultExpanded={
              grouped.length === 1 || instanceSortRank(instanceName) <= 1 || index === 0
            }
          />
        ))}

        {hasMore && onLoadMore && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-60 transition-colors"
            >
              {loadingMore ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>Carregar mais movimentações</>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="w-4 h-4 text-gray-600 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              Histórico de movimentações
            </h3>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {meta?.total != null
                  ? `${meta.total} movimentaç${meta.total === 1 ? "ão" : "ões"} — clique para expandir`
                  : "Histórico processual — clique para expandir"}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp
            className="w-5 h-5 text-gray-600 flex-shrink-0"
            aria-hidden
          />
        ) : (
          <ChevronDown
            className="w-5 h-5 text-gray-600 flex-shrink-0"
            aria-hidden
          />
        )}
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <div className="pt-4">{renderBody()}</div>
        </div>
      )}
    </div>
  );
}
